<template>
  <div>
    <ReviewCardAlert
      :rate="rate"
      :class-names="alertSpacingClass"
    />

    <template v-if="!rate.is_hidden_for_verification">
      <ReviewCardResolved
        v-if="!rate.is_deleted"
        :rate="rate"
      />

      <template v-if="showFullContent">
        <div
          class="d-flex flex-column"
          :class="isMobile ? 'ga-4' : 'ga-6'"
        >
          <ReviewCardSupplements
            v-if="!rate.is_refuted"
            :items="supplements"
          />
          <ReviewCardComments
            :sections="commentSections"
            :show-watermark="!isMobile"
            :limit-width="!isMobile"
          />
          <ReviewCardImages
            v-if="!rate.is_refuted"
            ref="imagesRef"
            :rate="rate"
            @open="onOpenImage"
          />
          <ReviewCardDoctor
            :rate="rate"
            :lpu="lpu"
          />
          <ReviewCardReply
            v-if="showLpuReply"
            :rate="rate"
            type="lpu"
          />
          <ReviewCardReply
            v-if="showModeratorReply"
            :rate="rate"
            type="moderator"
          />
        </div>
      </template>

      <div
        v-else-if="isPreview"
        :class="{ 'mt-5': hasContentAboveTruncated }"
      >
        <ReviewCardSupplements
          v-if="truncatedSupplement"
          :items="[truncatedSupplement]"
          :truncated="true"
        />
        <ReviewCardComments
          v-else-if="truncatedSection"
          :sections="[truncatedSection]"
          :truncated="true"
        />
        <button
          type="button"
          class="text-body-2 primary--text mt-4 button-reset"
          @click.stop="onReadMore"
        >
          {{ tStatic('Читать полностью') }}
        </button>
      </div>

      <ReviewCardFooter
        v-if="showFooter"
        :class="{
          'mt-4': isMobile,
          'mt-6': !isMobile && showFullContent,
        }"
        :rate="rate"
      />
    </template>

    <div
      v-if="!isMobile && (showAdminLink || showPultLink)"
      class="d-flex gc-4 mt-4"
    >
      <a
        v-if="showAdminLink"
        :href="adminHref"
        class="adminka d-inline-flex px-4 py-1 ma-0"
        target="_blank"
        rel="noopener noreferrer"
      >A</a>
      <a
        v-if="showPultLink"
        :href="pultHref"
        class="adminka d-inline-flex px-4 py-1 ma-0"
        target="_blank"
        rel="noopener noreferrer"
      >pult</a>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue'
import useIsMobile from 'src/composables/useIsMobile'
import useYandexGoals from 'src/composables/useYandexGoals'
import useContextStore from 'src/store/useContextStore'
import type { ContextStore } from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/index/types'
import type { Lpu } from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_types'
import type { Rate } from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuReviews/types'
import ReviewCardAlert from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuReviews/components/ReviewCardAlert.vue'
import ReviewCardComments from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuReviews/components/ReviewCardComments.vue'
import ReviewCardDoctor from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuReviews/components/ReviewCardDoctor.vue'
import ReviewCardFooter from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuReviews/components/ReviewCardFooter.vue'
import ReviewCardImages from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuReviews/components/ReviewCardImages.vue'
import ReviewCardReply from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuReviews/components/ReviewCardReply.vue'
import ReviewCardResolved from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuReviews/components/ReviewCardResolved.vue'
import ReviewCardSupplements from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuReviews/components/ReviewCardSupplements.vue'
import tStatic from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_functions/tStatic'
import type { CoreImageLightboxItem } from 'src/core/CoreImageLightbox/types'
import { getCommentSections } from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuReviews/functions/getCommentHtml'
import { getPublishedSupplements } from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuReviews/functions/getRateMeta'
import { hasReviewAlert } from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuReviews/functions/getReviewAlertText'

type ImagesExpose = {
  images: CoreImageLightboxItem[]
}

const props = defineProps<{
  rate: Rate
  lpu: Lpu
}>()

const emit = defineEmits(['open-image'])

const isMobile = useIsMobile()
const { context }: ContextStore = useContextStore()
const { sendYaGoal } = useYandexGoals()
const isFullText = ref(false)
const imagesRef = ref<ImagesExpose | null>(null)
const alertSpacingClass = computed(() => {
  if (isMobile.value) {
    return props.rate.is_deleted ? '' : 'mt-5'
  }

  return 'mb-4'
})
const showFullContent = computed(() => (
  !props.rate.is_deleted
  && (!isMobile.value || isFullText.value)
))
const showFooter = computed(() => (
  props.rate.is_deleted
  || showFullContent.value
))
const isPreview = computed(() => (
  !showFullContent.value
  && !props.rate.is_deleted
  && !props.rate.is_refuted
))
const hasContentAboveTruncated = computed(() => (
  hasReviewAlert(props.rate)
  || Boolean(props.rate.resolved && !props.rate.is_deleted)
))
const supplements = computed(() => getPublishedSupplements(props.rate))
const truncatedSupplement = computed(() => supplements.value[0])
const commentSections = computed(() => getCommentSections(props.rate, context))
const truncatedSection = computed(() => commentSections.value[0])
const showLpuReply = computed(() => {
  if (!props.rate.lpu_reply || props.rate.is_refuted) {
    return false
  }

  if (!props.rate.lpu_replied?.owner_id || !props.lpu.owner_id) {
    return true
  }

  return props.lpu.owner_id === props.rate.lpu_replied.owner_id
})
const showModeratorReply = computed(() => Boolean(props.rate.moderator && !props.rate.is_refuted))
const showAdminLink = computed(() => Boolean(
  context.user_has_view_rate_lpu || context.request.user.is_staff,
))
const showPultLink = computed(() => Boolean(context.user_has_pult_staff_api_view_group))
const adminHref = computed(() => (
  props.rate.doctor
    ? `/admin/doctors/rate/${props.rate.id}/`
    : `/admin/doctors/ratelpu/${props.rate.id}/`
))
const pultHref = computed(() => (
  props.rate.doctor
    ? `/calc/manage/review/doctor/${props.rate.id}/`
    : `/calc/manage/review/lpu/${props.rate.id}/`
))

function onReadMore() {
  sendYaGoal('reviews_list_click_read_more')
  isFullText.value = true
}

function onOpenImage(index: number) {
  emit('open-image', {
    items: imagesRef.value?.images || [],
    index,
  })
}
</script>
